import type { DeliveryDestination } from "@redotech/commerce-cloud-sdk/rpc/schema/get-delivery-estimates";
import { Currency, isCurrency } from "@redotech/money/currencies";
import {
  SfccStorefrontClientConfig,
  WidgetFees,
} from "@redotech/redo-storefront-widget-common/widget";
import {
  CoverageFeeCapability,
  DeliveryType,
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartAttributes,
  ShopifyCartChange,
  ShopifyCartItem,
  ShopifyClientType,
  ShopifyProduct,
  ShopifyProductJS,
  ShopifyShop,
} from "@redotech/shopify-client/ajax";
import { abortRace, abortTimeout } from "@redotech/util/abort";
import { log } from "../monitoring/log";
import { DELIVER_TO_STORE_CHECKED_SELECTOR } from "../site-config/selectors";
import {
  CityBeachCoverageFee,
  isCityBeachStorefront,
} from "./commercecloud-city-beach";

/** Cartridge version below which the RedoCoverage-* controllers aren't trusted. */
export const MIN_CARTRIDGE_VERSION = 1;
/** A hung cartridge must not stall widget bootstrap (the probe is awaited before the widget mounts). */
const COVERAGE_FEE_REQUEST_TIMEOUT = Temporal.Duration.from({ seconds: 10 });

function coverageFeeSignal(signal?: AbortSignal): AbortSignal {
  const timeout = abortTimeout(COVERAGE_FEE_REQUEST_TIMEOUT);
  return signal ? abortRace([signal, timeout]) : timeout;
}

/**
 * `RedoCoverage-Get` response. `adjustment` is informational only — the probe
 * gates on ok/version/enabled. `csrf` (cartridge v2) is the token Add/Remove
 * must carry; a v1 cartridge sends none and accepts none.
 */
interface RedoCoverageGetResponse {
  ok: boolean;
  version: number;
  enabled: boolean;
  adjustment?: unknown;
  csrf?: { name: string; token: string };
}

/** Hand-written guards rather than zod: this runs in the storefront's critical load path. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseCsrf(value: unknown): { name: string; token: string } | null {
  return isRecord(value) &&
    typeof value.name === "string" &&
    typeof value.token === "string"
    ? { name: value.name, token: value.token }
    : null;
}

function parseRedoCoverageGetResponse(
  body: unknown,
): RedoCoverageGetResponse | null {
  if (
    !isRecord(body) ||
    typeof body.ok !== "boolean" ||
    typeof body.version !== "number" ||
    typeof body.enabled !== "boolean"
  ) {
    return null;
  }
  const csrf = body.csrf === undefined ? null : parseCsrf(body.csrf);
  if (body.csrf !== undefined && !csrf) {
    return null;
  }
  return {
    ok: body.ok,
    version: body.version,
    enabled: body.enabled,
    adjustment: body.adjustment,
    ...(csrf ? { csrf } : {}),
  };
}

/** Add/Remove response: `changed` is the server's own diff. `adjustment` is Add's current adjustment; Remove never sends it. */
interface RedoCoverageMutationResponse {
  ok: boolean;
  changed: boolean;
  adjustment?: unknown;
}

function parseRedoCoverageMutationResponse(
  body: unknown,
): RedoCoverageMutationResponse | null {
  return isRecord(body) &&
    typeof body.ok === "boolean" &&
    typeof body.changed === "boolean"
    ? { ok: body.ok, changed: body.changed, adjustment: body.adjustment }
    : null;
}

/** The only field of `RedoCoverage-Get`'s `adjustment` cartGet() needs, parsed defensively. */
function adjustmentAmount(adjustment: unknown): number | undefined {
  return isRecord(adjustment) && typeof adjustment.amount === "number"
    ? adjustment.amount
    : undefined;
}

const SFCC_REDO_PRODUCT_ID = "x-redo"; // SFCC uses skus as ids which we mgr doesnt support yet. "x-redo" string, hopefully unique enough.

/** `Sites-<id>-Site/<locale>` inside any SFCC controller URL or path. */
const SFCC_CONTROLLER_PATH_PATTERN =
  /\/on\/demandware\.store\/(Sites-[^/]+-Site)\/([^/]+)/;
/** Site id / locale must be a bare token so the built URL is safe and valid. */
const SFCC_TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/;
/** Checkout-page elements that may carry a controller URL Redo reads the base from. */
const SFCC_CARRIER_SELECTOR =
  "form[action], a[href], [data-url], [data-action-url]";
const SFCC_CARRIER_ATTRS = [
  "action",
  "href",
  "data-url",
  "data-action-url",
] as const;

/** Minimal shape of the SFCC `Cart-Get` response — only the fields we read. */
interface SfccMoney {
  value?: number;
  currency?: string;
}

interface SfccCartItem {
  id?: string;
  masterPid?: string;
  productName?: string;
  brand?: string;
  quantity?: number;
  UUID?: string;
  sku?: string;
  price?: { sales?: SfccMoney; list?: SfccMoney; min?: { sales?: SfccMoney } };
  /**
   * Post-discount line total. `finalDiscountedPrice` is a merchant
   * customization (Marc Jacobs); base SFRA only has formatted strings here.
   */
  priceTotal?: { finalDiscountedPrice?: number };
}

export interface SfccCart {
  items?: SfccCartItem[];
  totals?: {
    grandTotalValue?: number;
    /** Standard SFRA order-level (coupon/promotion) discount total. */
    orderLevelDiscountTotal?: { value?: number };
    /** Set by Redo's totals-model template change; the fee is then NOT folded into orderLevelDiscountTotal. */
    redoCoverageFee?: { value?: number };
  };
  shipments?: {
    shippingAddress?: {
      postalCode?: string | null;
      city?: string | null;
      stateCode?: string | null;
      countryCode?: { value?: string | null } | string | null;
    } | null;
  }[];
}

/**
 * Commerce Cloud (SFCC) StoreFront Client
 * Implements ExtensionShopifyClient interface for Salesforce Commerce Cloud integration
 */
export class CommercecloudStorefrontClient implements ExtensionShopifyClient {
  type = ShopifyClientType.COMMERCE_CLOUD;
  private readonly cartUrl: string;
  /** Cached RedoCoverage-Get parse; null on any failure. cartGet() reads this (if already probed) to net the fee out of pricing. */
  private coverageFeeProbe?: Promise<RedoCoverageGetResponse | null>;
  /** Set on City Beach hosts, whose own Redo-*Adjustment controllers replace the Redo cartridge. */
  private readonly cityBeachFee?: CityBeachCoverageFee;
  readonly coverageFee: CoverageFeeCapability;

  constructor({
    locale,
    config,
  }: {
    storeUrl: string;
    locale?: string;
    config?: SfccStorefrontClientConfig;
  }) {
    // Priority: cartridge-set redoStorefront.sfcc, then autocheck config.
    const cartridge = globalThis.redoStorefront?.sfcc;
    const siteId = CommercecloudStorefrontClient.resolveSiteId(config);
    const resolvedLocale = cartridge?.locale ?? locale ?? config?.locale;
    this.cartUrl = `/on/demandware.store/Sites-${siteId ?? ""}-Site/${resolvedLocale ?? ""}`;
    // Test doubles stub location without a hostname.
    const hostname: string | undefined = globalThis.location.hostname;
    if (hostname && isCityBeachStorefront(hostname)) {
      this.cityBeachFee = new CityBeachCoverageFee(() =>
        this.coverageFeeControllerBase(),
      );
    }
    this.coverageFee = this.cityBeachFee ?? this.cartridgeCoverageFee;
  }

  get usesCityBeachAdjustments(): boolean {
    return this.cityBeachFee !== undefined;
  }

  /** Custom fee attach/remove via the cartridge's RedoCoverage-* controllers. */
  private readonly cartridgeCoverageFee: CoverageFeeCapability = {
    isAvailable: async (signal?: AbortSignal): Promise<boolean> => {
      if (!this.coverageFeeProbe) {
        this.coverageFeeProbe = this.probeCoverageFee(signal);
      }
      const result = await this.coverageFeeProbe;
      return (
        result !== null &&
        result.ok &&
        result.enabled &&
        result.version >= MIN_CARTRIDGE_VERSION
      );
    },
    attach: (input, signal) => this.attachCoverageFee(input, signal),
    // widgetSlug in `input` is BigCommerce-only; the cartridge fee is
    // per-cart, not per-widget, so it's accepted but unused here.
    remove: (_input, signal) => this.removeCoverageFee(signal),
  };

  private coverageFeeControllerBase(): string {
    return (
      CommercecloudStorefrontClient.resolveControllerBase() ?? this.cartUrl
    );
  }

  /** Fails closed to null on any throw, non-200, or bad body; ok/enabled/version are checked by isAvailable(). */
  private async probeCoverageFee(
    signal?: AbortSignal,
  ): Promise<RedoCoverageGetResponse | null> {
    try {
      const response = await fetch(
        `${this.coverageFeeControllerBase()}/RedoCoverage-Get`,
        { signal: coverageFeeSignal(signal) },
      );
      if (!response.ok) {
        return null;
      }
      return parseRedoCoverageGetResponse(
        await response.json().catch(() => null),
      );
    } catch {
      return null;
    }
  }

  private async attachCoverageFee(
    input: {
      productType: string;
      feeAmount: number;
      label: string;
      widgetSlug?: string;
    },
    signal?: AbortSignal,
  ): Promise<{ changed: boolean }> {
    const result = await this.coverageFeeMutation(
      "Add",
      {
        type: input.productType,
        amount: input.feeAmount.toString(),
        label: input.label,
      },
      signal,
    );
    if (result) {
      await this.refreshCoverageFeeProbeAdjustment(result.adjustment);
    }
    return { changed: result?.changed ?? false };
  }

  private async removeCoverageFee(
    signal?: AbortSignal,
  ): Promise<{ changed: boolean }> {
    const result = await this.coverageFeeMutation("Remove", {}, signal);
    if (result) {
      await this.refreshCoverageFeeProbeAdjustment(null);
    }
    return { changed: result?.changed ?? false };
  }

  /**
   * POSTs a form to RedoCoverage-<route> with the CSRF token from the cached
   * probe (cartridge v2). A 403 `csrf` answer means the token went stale, so
   * the probe is fetched again and the request retried once. Null on any
   * failure — the caller then reports `changed: false`.
   */
  private async coverageFeeMutation(
    route: "Add" | "Remove",
    fields: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<RedoCoverageMutationResponse | null> {
    for (let attempt = 0; attempt < 2; attempt++) {
      const csrf = (await this.coverageFeeProbe)?.csrf;
      let response: Response;
      try {
        response = await fetch(
          `${this.coverageFeeControllerBase()}/RedoCoverage-${route}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams(
              csrf ? { ...fields, [csrf.name]: csrf.token } : fields,
            ),
            signal: coverageFeeSignal(signal),
          },
        );
      } catch (error) {
        log(
          `RedoCoverage-${route} request failed, leaving the fee unchanged`,
          error,
        );
        return null;
      }
      if (response.status === 403) {
        const body = await response.json().catch(() => null);
        if (
          attempt === 0 &&
          CommercecloudStorefrontClient.isCsrfRejection(body)
        ) {
          log(`RedoCoverage-${route} rejected the CSRF token, re-probing once`);
          if (await this.refreshProbe(signal)) {
            continue;
          }
          return null;
        }
        return null;
      }
      return CommercecloudStorefrontClient.parseMutationResponse(response);
    }
    return null;
  }

  private static isCsrfRejection(body: unknown): boolean {
    return isRecord(body) && body.error === "csrf";
  }

  /** Null on HTTP/body failure or ok:false; `changed` isn't inferred from HTTP status alone. */
  private static async parseMutationResponse(
    response: Response,
  ): Promise<RedoCoverageMutationResponse | null> {
    if (!response.ok) {
      return null;
    }
    const parsed = parseRedoCoverageMutationResponse(
      await response.json().catch(() => null),
    );
    return parsed?.ok ? parsed : null;
  }

  /** Re-probes and replaces the cache only on success, so a failed
   *  re-probe can never poison a previously-good cached result. */
  private async refreshProbe(
    signal?: AbortSignal,
  ): Promise<RedoCoverageGetResponse | null> {
    const result = await this.probeCoverageFee(signal);
    if (result) {
      this.coverageFeeProbe = Promise.resolve(result);
    } else {
      log("SFCC coverage probe refresh failed, keeping last known state");
    }
    return result;
  }

  /**
   * Refreshes the cached probe's adjustment after attach/remove so cartGet()
   * nets the live fee. A response that omits `adjustment` says nothing about
   * the basket, so the probe is re-fetched rather than assumed empty. No-ops
   * if never probed.
   */
  private async refreshCoverageFeeProbeAdjustment(
    adjustment: unknown,
  ): Promise<void> {
    if (!this.coverageFeeProbe) {
      return;
    }
    const previous = await this.coverageFeeProbe;
    if (!previous) {
      return;
    }
    if (adjustment === undefined) {
      await this.refreshProbe();
      return;
    }
    this.coverageFeeProbe = Promise.resolve({ ...previous, adjustment });
  }

  /** Site id from the cartridge-set `redoStorefront.sfcc`, then the autocheck config. */
  static resolveSiteId(
    config?: SfccStorefrontClientConfig,
  ): string | undefined {
    return globalThis.redoStorefront?.sfcc?.siteId ?? config?.siteId;
  }

  async cartAdd(
    data: { items: Partial<ShopifyCartItem>[] },
    params?: URLSearchParams,
    preventReload?: boolean,
  ): Promise<void> {
    if (data.items.length !== 1) {
      return;
    }

    const item = data.items[0];
    if (!item?.id && !item?.variant_id) {
      throw new Error("Item ID or variant ID is required");
    }

    let productId = String(item.id || item.variant_id);
    if (productId === SFCC_REDO_PRODUCT_ID) {
      productId = "x-redo";
    }
    const quantity = item.quantity || 1;

    await fetch(`${this.cartUrl}/Cart-AddProduct`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        pid: productId,
        quantity: quantity.toString(),
      }),
    });
  }

  async cartGet(signal?: AbortSignal): Promise<ShopifyCart> {
    const response = await fetch(`${this.cartUrl}/Cart-Get`, { signal });
    const data = await this.parseCartResponse(response);
    const sfccItems = data.items ?? [];

    const attachedFeeAmount = await this.attachedCoverageFeeAmount();
    const attachedFeeCents = Math.round((attachedFeeAmount ?? 0) * 100);

    const preDiscountLinePrices = sfccItems.map(
      (item) =>
        Number(
          item.price?.sales?.value ||
            item.price?.list?.value || // In practice I have only seen item.price.list: null, but I expect it will be there as a full number sometimes
            item.price?.min?.sales?.value ||
            0,
        ) *
        (item.quantity || 0) *
        100,
    );
    const discountedLineTotals = sfccItems.map((item) => {
      const discountedLineTotal = item.priceTotal?.finalDiscountedPrice;
      return typeof discountedLineTotal === "number" &&
        Number.isFinite(discountedLineTotal)
        ? Math.round(discountedLineTotal * 100)
        : undefined;
    });
    // Stock SFRA folds an attached fee into orderLevelDiscountTotal as a negative
    // discount; adding the fee back nets it out. Totals that expose redoCoverageFee already exclude it.
    const feeFoldedIntoDiscountTotal =
      attachedFeeCents > 0 && data.totals?.redoCoverageFee === undefined;
    const orderLevelDiscountCents = Math.round(
      ((Number(data.totals?.orderLevelDiscountTotal?.value) || 0) +
        (feeFoldedIntoDiscountTotal ? (attachedFeeAmount ?? 0) : 0)) *
        100,
    );
    const discountedLinePrices =
      CommercecloudStorefrontClient.allocateOrderLevelDiscount(
        CommercecloudStorefrontClient.lineTotalsBeforeOrderDiscount(
          preDiscountLinePrices,
          discountedLineTotals,
          attachedFeeCents,
        ),
        orderLevelDiscountCents,
      );

    const items: ShopifyCartItem[] = sfccItems.map((item, i) => ({
      id: item.id || "",
      line_id: item.UUID, // UUID is a 26 character hex string, so do simple hash
      product_id: item.masterPid || item.id || "",
      variant_id: item.id || "",
      handle: item.id || "",
      title: item.productName || "",
      vendor: item.brand || "",
      quantity: item.quantity || 0,
      requires_shipping: true,
      line_price: discountedLinePrices[i],
      original_line_price: preDiscountLinePrices[i],
      properties: null,
      sku: item.sku || "",
    }));

    if (items.some((item) => item.original_line_price === 0)) {
      log("WARN: Item has line price of 0");
    }

    // Stock SFRA totals are formatted strings; numeric grandTotalValue is a
    // site customization. Fall back to the items subtotal when it's absent.
    let totalPrice = data.totals?.grandTotalValue;
    // Only the numeric grand total carries the attached fee; the items
    // fallback below is already fee-netted, so the fee must not be reported.
    const totalIncludesAttachedFee = totalPrice !== undefined;
    if (totalPrice === undefined) {
      log("WARN: SFCC cart missing totals.grandTotalValue", {
        totals: data.totals,
      });
      totalPrice =
        items.reduce((acc, item) => acc + (item.line_price ?? 0), 0) / 100;
    }

    return {
      attributes: {},
      items,
      // Prefer the basket's ISO currency (SFCC prices are localized) over the
      // resolved market currency, so non-USD stores aren't mislabeled "USD".
      currency:
        CommercecloudStorefrontClient.getCartCurrency(data) ??
        CommercecloudStorefrontClient.getCurrencyFromPathname(),
      total_price: totalPrice,
      // Only while a fee is attached and included in total_price; product-mode
      // teams never probe, so their cartGet() output stays byte-identical.
      ...(attachedFeeCents > 0 && totalIncludesAttachedFee
        ? { coverage_fee: attachedFeeAmount }
        : {}),
    };
  }

  /**
   * Line totals (cents) to prorate the order-level discount over. SFRA folds
   * an attached fee into every line's finalDiscountedPrice in proportion to
   * the line total, so it is taken back out the same way and line-level
   * discounts survive. A line that lacks finalDiscountedPrice falls back to
   * its sales price, which never carried the fee, so only lines reporting a
   * discounted total have their fee share removed.
   */
  static lineTotalsBeforeOrderDiscount(
    preDiscountLinePrices: number[],
    discountedLineTotals: (number | undefined)[],
    attachedFeeCents: number,
  ): number[] {
    const lineTotals = discountedLineTotals.map(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (total, i) => total ?? preDiscountLinePrices[i]!,
    );
    if (attachedFeeCents <= 0) {
      return lineTotals;
    }
    const netted = CommercecloudStorefrontClient.allocateOrderLevelDiscount(
      lineTotals,
      attachedFeeCents,
    );
    return lineTotals.map((total, i) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      discountedLineTotals[i] === undefined ? total : netted[i]!,
    );
  }

  /**
   * Prorate an order-level discount across line totals (all cents), the way
   * Shopify allocates cart-level discounts into line costs.
   */
  static allocateOrderLevelDiscount(
    linePrices: number[],
    discount: number,
  ): number[] {
    const subtotal = linePrices.reduce((sum, price) => sum + price, 0);
    if (discount <= 0 || subtotal <= 0) {
      return linePrices;
    }
    let remainingDiscount = Math.min(discount, subtotal);
    let remainingSubtotal = subtotal;
    return linePrices.map((price) => {
      const share =
        remainingSubtotal > 0
          ? Math.round((price * remainingDiscount) / remainingSubtotal)
          : 0;
      remainingDiscount -= share;
      remainingSubtotal -= price;
      return price - share;
    });
  }

  /** RedoCoverage-Get's `adjustment` is untyped/opaque; only trust a shape carrying a numeric `amount`. */
  static extractAdjustmentAmount(adjustment: unknown): number | undefined {
    return adjustmentAmount(adjustment);
  }

  /** Only reads an already-kicked-off probe; product-mode teams never probe, so this resolves undefined. */
  private async attachedCoverageFeeAmount(): Promise<number | undefined> {
    if (this.cityBeachFee) {
      return this.cityBeachFee.attachedFeeAmount();
    }
    return this.coverageFeeProbe
      ? CommercecloudStorefrontClient.extractAdjustmentAmount(
          (await this.coverageFeeProbe)?.adjustment,
        )
      : undefined;
  }

  protected parseCartResponse(response: Response): Promise<SfccCart> {
    return response.json();
  }

  protected async fetchCart(
    signal?: AbortSignal,
  ): Promise<SfccCart | undefined> {
    const controllerBase =
      CommercecloudStorefrontClient.resolveControllerBase() ?? this.cartUrl;
    const response = await fetch(`${controllerBase}/Cart-Get`, { signal });
    if (!response.ok) {
      return undefined;
    }
    return this.parseCartResponse(response);
  }

  async getShippingDestination(
    signal?: AbortSignal,
  ): Promise<DeliveryDestination | undefined> {
    const data = await this.fetchCart(signal);
    if (!data) {
      return undefined;
    }
    return CommercecloudStorefrontClient.getDestinationFromCart(data);
  }

  /**
   * Piggyback on the checkout's single Cart-Get: return the shipping destination
   * plus whether any cart line carries a configured suppression field.
   */
  async getDeliveryEstimateCartContext(
    suppressFields: string[],
    signal?: AbortSignal,
  ): Promise<{
    destination: DeliveryDestination | undefined;
    suppress: boolean;
  }> {
    const data = await this.fetchCart(signal);
    if (!data) {
      return { destination: undefined, suppress: false };
    }
    return {
      destination: CommercecloudStorefrontClient.getDestinationFromCart(data),
      suppress: CommercecloudStorefrontClient.cartHasSuppressedField(
        data,
        suppressFields,
      ),
    };
  }

  async cartChange(
    change: ShopifyCartChange,
    signal?: AbortSignal,
  ): Promise<void> {
    // Handle quantity update
    if (change.quantity !== undefined) {
      const params = new URLSearchParams();
      params.append("pid", String(change.id || ""));
      params.append("uuid", String(change.line || ""));
      params.append("quantity", String(change.quantity));

      await fetch(`${this.cartUrl}/Cart-UpdateQuantity?${params}`, { signal });
    } else {
      // Handle item removal (quantity 0 case)
      const params = new URLSearchParams();
      params.append("pid", String(change.id || ""));
      params.append("uuid", String(change.line || ""));

      await fetch(`${this.cartUrl}/Cart-RemoveProductLineItem?${params}`, {
        signal,
      });
    }

    location.reload();
  }

  async removeItemFromCart(
    item: ShopifyCartItem,
    numToRemove: number,
    signal?: AbortSignal,
  ): Promise<undefined> {
    const params = new URLSearchParams();
    params.append("pid", String(item.variant_id));
    params.append("uuid", String(item.line_id || item.id));

    if (numToRemove >= item.quantity) {
      // Remove entire item
      await fetch(`${this.cartUrl}/Cart-RemoveProductLineItem?${params}`, {
        signal,
      });
    } else {
      // Update quantity
      const newQuantity = item.quantity - numToRemove;
      params.append("quantity", String(newQuantity));
      await fetch(`${this.cartUrl}/Cart-UpdateQuantity?${params}`, { signal });
    }
  }

  async cartClear(): Promise<void> {
    return;
  }

  async updateCartAttribute(
    attributes: ShopifyCartAttributes,
    signal?: AbortSignal,
  ): Promise<ShopifyCart | void> {
    log(
      "updateCartAttribute() was called, but is unsupported for Commerce Cloud.",
    );
    return;
  }

  async updateCartItemSellingPlan(
    item: ShopifyCartItem,
    sellingPlanId: number | null,
    signal?: AbortSignal,
  ): Promise<undefined> {
    log(
      "updateCartItemSellingPlan() was called, but is unsupported for Commerce Cloud.",
    );
  }

  async getCollections(
    handle?: string,
    signal?: AbortSignal,
  ): Promise<{ title: string; handle: string }[] | undefined> {
    log(
      "updateCartItemSellingPlan() was called, but is unsupported for Commerce Cloud.",
    );

    return [];
  }

  async productGet(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProduct> {
    throw new Error("Unsupported");
  }

  async productGetJS(
    handle: string,
    signal?: AbortSignal,
  ): Promise<ShopifyProductJS> {
    throw new Error("Unsupported");
  }

  async getShopMetafield(
    data: { key: string; namespace: string },
    signal?: AbortSignal,
  ): Promise<ShopifyShop | undefined> {
    throw new Error("Unsupported");
  }

  async deleteCartAttribute(
    data: { key: string },
    signal?: AbortSignal,
  ): Promise<void> {
    log(
      "deleteCartAttribute() was called, but is unsupported for Commerce Cloud.",
    );
  }

  static getProductJsFromFees = ({
    fees,
  }: {
    fees: WidgetFees;
  }): ShopifyProductJS => {
    const mainProduct = {
      return: fees.returnProduct,
      package_protection: fees.packageProduct,
      "return,package_protection": fees.bothProduct,
    }[fees.mainType];

    if (!mainProduct) {
      throw new Error("Main product not found");
    }

    return {
      id: SFCC_REDO_PRODUCT_ID,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      price_min: Number(mainProduct!.variants![0]!.price) * 100,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      price_max: Number(mainProduct!.variants![0]!.price) * 100,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      variants: mainProduct!.variants!.map((variant) => ({
        id: variant.variant_id,
        price: Number(variant.price) * 100,
        requires_shipping: true,
      })),
    };
  };

  static isOnCheckoutPage(): boolean {
    // SFCC controller URLs are capitalized (e.g. /en_US/Checkout-Begin), so match case-insensitively.
    return location.pathname.toLowerCase().includes("/checkout");
  }

  static isOnConfirmationPage(): boolean {
    // Matches SEO-mapped routes (/orderconfirmation) and the raw controller
    // URL (.../Order-Confirm); distinct from the "/checkout" match above.
    const pathname = location.pathname.toLowerCase();
    return (
      pathname.includes("orderconfirmation") ||
      pathname.includes("order-confirm")
    );
  }

  static isOnOrderDetailsPage(): boolean {
    // Account order-details route (`.../orderdetail?orderId=…`); neither this nor
    // "orderconfirmation" is a substring of the other, so the two never cross-match.
    return location.pathname.toLowerCase().includes("orderdetail");
  }

  static isOnOrderHistoryPage(): boolean {
    // Account order-history route (`.../orders`); boundary-matched so it stays
    // disjoint from `orderdetail`/`orderconfirmation`, which lack a `/orders` segment.
    return /\/orders(?:\/|$)/.test(location.pathname.toLowerCase());
  }

  static isOnPaymentSection(): boolean {
    return window.location.hash.includes("#payment");
  }

  static getCheckoutDeliveryType(): DeliveryType {
    if (!CommercecloudStorefrontClient.isOnCheckoutPage()) {
      return DeliveryType.UNKNOWN_OR_UNSELECTED;
    }

    if (document.querySelector(DELIVER_TO_STORE_CHECKED_SELECTOR)) {
      return DeliveryType.IN_STORE_PICKUP;
    }

    return DeliveryType.UNKNOWN_OR_UNSELECTED;
  }

  /**
   * Pull the basket's ISO currency code out of an SFCC `Cart-Get` response. The
   * SFRA price model carries `currency` (e.g. "AUD") alongside the price value;
   * we scan the line items since the cart model has no single top-level field.
   */
  static getCartCurrency(data: SfccCart): Currency | undefined {
    for (const item of data.items ?? []) {
      const candidates = [
        item.price?.sales?.currency,
        item.price?.list?.currency,
        item.price?.min?.sales?.currency,
      ];
      const currency = candidates.find(
        (code): code is Currency =>
          typeof code === "string" && isCurrency(code),
      );
      if (currency) {
        return currency;
      }
    }
    return undefined;
  }

  /**
   * Derive the current site's SFCC controller base from the page pathname,
   * preferring the live page's site/locale over the configured `this.cartUrl`.
   */
  static getControllerBaseFromPathname(pathname: string): string | undefined {
    const match = pathname.match(
      /^\/on\/demandware\.store\/(Sites-[^/]+-Site)\/([^/]+)/,
    );
    if (!match) {
      return undefined;
    }
    return `/on/demandware.store/${match[1]}/${match[2]}`;
  }

  /**
   * Controller base for SFCC requests, preferring the live page over the
   * configured `this.cartUrl`: pathname, then cartridge `redoStorefront.sfcc`,
   * then the DOM.
   */
  static resolveControllerBase(): string | undefined {
    const fromPathname =
      CommercecloudStorefrontClient.getControllerBaseFromPathname(
        location.pathname,
      );
    if (fromPathname) {
      return fromPathname;
    }

    const sfcc = globalThis.redoStorefront?.sfcc;
    if (
      typeof sfcc?.siteId === "string" &&
      typeof sfcc.locale === "string" &&
      SFCC_TOKEN_PATTERN.test(sfcc.siteId) &&
      SFCC_TOKEN_PATTERN.test(sfcc.locale)
    ) {
      return `/on/demandware.store/Sites-${sfcc.siteId}-Site/${sfcc.locale}`;
    }

    if (typeof document === "undefined") {
      return undefined;
    }
    return CommercecloudStorefrontClient.getControllerBaseFromCarrierElement(
      document,
    );
  }

  /**
   * Extract the controller base from the first checkout element carrying an SFCC
   * controller URL (form action / link / data attribute).
   */
  static getControllerBaseFromCarrierElement(
    root: ParentNode,
  ): string | undefined {
    for (const element of root.querySelectorAll(SFCC_CARRIER_SELECTOR)) {
      for (const attr of SFCC_CARRIER_ATTRS) {
        const match = element
          .getAttribute(attr)
          ?.match(SFCC_CONTROLLER_PATH_PATTERN);
        if (match) {
          return `/on/demandware.store/${match[1]}/${match[2]}`;
        }
      }
    }
    return undefined;
  }

  /**
   * Read the shipping destination from an SFCC `Cart-Get` response, normalizing
   * `countryCode` (string or `{ value }`, maybe lowercase) to an uppercase ISO code.
   */
  static getDestinationFromCart(
    data: SfccCart | null | undefined,
  ): DeliveryDestination | undefined {
    const address = data?.shipments?.[0]?.shippingAddress;
    if (!address) {
      return undefined;
    }

    const rawCountry =
      typeof address.countryCode === "string"
        ? address.countryCode
        : address.countryCode?.value;
    const countryCode = rawCountry?.trim().toUpperCase();
    if (!countryCode) {
      return undefined;
    }

    const postalCode = address.postalCode?.trim();
    const city = address.city?.trim();
    const state = address.stateCode?.trim();

    return {
      countryCode,
      ...(postalCode ? { postalCode } : {}),
      ...(city ? { city } : {}),
      ...(state ? { state } : {}),
    };
  }

  /**
   * True when any cart line carries a truthy value on any configured field.
   * Lines are read as raw records since these fields sit outside `SfccCartItem`.
   */
  static cartHasSuppressedField(
    data: SfccCart,
    suppressFields: string[],
  ): boolean {
    if (suppressFields.length === 0) {
      return false;
    }
    for (const item of data.items ?? []) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const record = item as Record<string, unknown>;
      for (const field of suppressFields) {
        if (record[field]) {
          return true;
        }
      }
    }
    return false;
  }

  static getCurrencyFromPathname = (): Currency => {
    if (
      location.pathname.startsWith("/us") ||
      location.pathname.startsWith("/global")
    ) {
      return Currency.USD;
    } else if (location.pathname.startsWith("/nz")) {
      return Currency.NZD;
    } else if (location.pathname.startsWith("/au")) {
      return Currency.AUD;
    }

    return Currency.AUD;
  };
}
